import {
  Box,
  Card,
  CardBody,
  Grid,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import ReportDataTable, { type ReportColumn } from './ReportDataTable'
import { KPI_BREAKDOWN_COLUMNS, type DetailRow } from './kpiReportColumns'
import type { KpiMetricBreakdownRow } from '@/lib/services/helpdeskReportService'
import type { MetricRow } from './reportTableColumns'

export type KpiDetailTab = {
  label: string
  columns: ReportColumn<DetailRow>[]
  rows: DetailRow[]
  total: number
  shown: number
  rowKey: (row: DetailRow, index: number) => string | number
}

type Props = {
  loading: boolean
  periodLabel: string
  thresholdNote?: string | null
  summaryRows: MetricRow[]
  breakdownRows: KpiMetricBreakdownRow[]
  detailTabs: KpiDetailTab[]
}

export default function KpiReportLayout({
  loading,
  periodLabel,
  thresholdNote,
  summaryRows,
  breakdownRows,
  detailTabs,
}: Props) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={1}>
        Showing data for: {periodLabel}
      </Text>
      {thresholdNote ? (
        <Text fontSize="xs" color="gray.500" mb={4}>
          Thresholds: {thresholdNote}
        </Text>
      ) : (
        <Box mb={4} />
      )}

      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }}
        gap={3}
        mb={4}
      >
        {loading
          ? Array.from({ length: Math.max(summaryRows.length, 4) }).map((_, i) => (
              <Skeleton key={i} height="80px" />
            ))
          : summaryRows.map((row) => (
              <Card key={row.metric}>
                <CardBody py={3}>
                  <Stat>
                    <StatLabel fontSize="xs" noOfLines={2}>
                      {row.metric}
                    </StatLabel>
                    <StatNumber fontSize="xl">{row.value}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
            ))}
      </Grid>

      <Box mb={4}>
        <ReportDataTable
          title="How totals are calculated"
          subtitle="Each rate is derived from met count ÷ eligible total for the current filters"
          columns={KPI_BREAKDOWN_COLUMNS}
          rows={breakdownRows}
          loading={loading}
          loadingHeight="180px"
          getRowKey={(row) => row.key}
        />
      </Box>

      {detailTabs.length > 0 && (
        <Tabs colorScheme="blue" variant="line">
          <TabList mb={2} flexWrap="wrap">
            {detailTabs.map((tab) => (
              <Tab key={tab.label} fontSize="sm" fontWeight="600">
                {tab.label}
                {!loading && tab.total > 0 ? ` (${tab.total})` : ''}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {detailTabs.map((tab) => (
              <TabPanel px={0} key={tab.label}>
                <ReportDataTable
                  title={`${tab.label} detail`}
                  subtitle={
                    !loading && tab.total > 0
                      ? `Showing ${tab.shown} of ${tab.total} records (max 100 per tab)`
                      : undefined
                  }
                  columns={tab.columns}
                  rows={tab.rows}
                  loading={loading}
                  loadingHeight="240px"
                  emptyMessage="No records match the current filters."
                  getRowKey={tab.rowKey}
                />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  )
}
